import { useEffect, useContext, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { SessionContext } from "../context/SessionContext";
import { ProfileContext } from "../context/ProfileContext";
import { toast } from "react-hot-toast";
import { format } from "date-fns";

import Select from "react-select";
import ToastDisplay from "../components/ToastDisplay";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Pagination from "../components/Pagination";
import PageLoader from "../components/PageLoader";
import Empty from "../components/Empty";

const AnnouncementRecipients = ({ announcement }) => {
	const navigate = useNavigate();
	const [session] = useContext(SessionContext);
	const [profile] = useContext(ProfileContext);
	const [messages, setMessages] = useState([]);
	const [meta, setMeta] = useState(null);
	const [searchParams, setSearchParams] = useSearchParams({});
	const [filterModal, setFilterModal] = useState(false);
	const [showFilters, setShowFilters] = useState(false);
	const [loading, setLoading] = useState(true);
	const [queued, setQueued] = useState(0);
	const [sent, setSent] = useState(0);
	const [failed, setFailed] = useState(0);

	const [searchQ, setSearchQ] = useState(searchParams.get("q") || "");
	const [searchStatus, setSearchStatus] = useState(
		searchParams.get("status") || ""
	);

	const searchFields = [
		{ key: "q", value: searchQ },
		{ key: "status", value: searchStatus },
	];

	const filterFields = [
		{
			key: "status",
			value: searchStatus,
			defaultVal: "",
			edit: setSearchStatus,
		},
	];

	const getData = async () => {
		setLoading(true);
		const requestOptions = {
			method: "GET",
			headers: { "X-Auth-Token": session },
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/announcements/a/${announcement?.id}/messages`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content="Something went wrong." />);
		} else {
			setMessages(data.messages);
			setMeta(data.meta);
			setQueued(data.queued);
			setSent(data.sent);
			setFailed(data.failed);
		}
		setLoading(false);
	};

	const search = (e) => {
		e.preventDefault();
		var newSearch = { ...searchParams };
		searchFields.map((field) => {
			if (field.value !== "") {
				newSearch[field.key] = field.value;
			} else {
				delete newSearch[field.key];
			}
		});
		setSearchParams(newSearch);
		setFilterModal(false);
	};

	const removeFilter = (e, func, key) => {
		e.preventDefault();
		func("");
		var newSearch = { ...searchParams };
		searchFields.map((field) => {
			if (field.value !== "" && field.key !== key) {
				newSearch[field.key] = field.value;
			} else {
				delete newSearch[field.key];
			}
		});
		setSearchParams(newSearch);
	};

	const clearFilters = (e) => {
		e.preventDefault();
		setSearchParams({});
		filterFields.map((field) => {
			field.edit(field.defaultVal);
		});
	};

	const isEmpty = (value) => {
		return value === "" || value === null || value === undefined;
	};

	useEffect(() => {
		document.title = "Announcements | Pisara Dashboard";
		if (profile) {
			if (!profile.role.announcements_view) {
				navigate("/");
			} else {
				getData();
				if (filterFields.every((field) => isEmpty(field.value))) {
					setShowFilters(false);
				} else {
					setShowFilters(true);
				}
			}
		}
	}, [searchParams, profile]);

	return (
		<>
			<Row className="mb-20">
				<Col>
					<div className="announcement-stat bg-white rounded-8 flex-column border">
						<span className="text-xl text-medium-inter mb-1">
							{queued?.toLocaleString()}
						</span>
						<span className="text-sm text-medium-inter opacity-7">Queued</span>
					</div>
				</Col>
				<Col>
					<div className="announcement-stat bg-white rounded-8 flex-column border">
						<span className="text-xl text-medium-inter mb-1">
							{sent?.toLocaleString()}
						</span>
						<span className="text-sm text-medium-inter opacity-7">Sent</span>
					</div>
				</Col>
				<Col>
					<div className="announcement-stat bg-white rounded-8 flex-column border">
						<span className="text-xl text-medium-inter mb-1">
							{failed?.toLocaleString()}
						</span>
						<span className="text-sm text-medium-inter opacity-7">Failed</span>
					</div>
				</Col>
			</Row>
			<div className="bg-white rounded-8 p-20 mb-30">
				<Row className="content-header flex-row-left mb-20">
					<Col>
						<h1 className="text-xl m-0">Recipients</h1>
					</Col>
				</Row>
				<div className="announcement-recipient-header border-bottom full mb-10">
					<div className="flex-row-left mb-15 full">
						<Col className="col-6">
							<Form onSubmit={search}>
								<div className="search-container bg-gray flex-row-left">
									<i className="ri-search-line search-icon" />
									<Form.Control
										className="text-sm"
										placeholder="Search recipients"
										value={searchQ}
										onChange={(e) => setSearchQ(e.target.value)}
									/>
									<Button
										variant="light"
										className="filter-btn flex-column"
										onClick={() => setFilterModal(true)}
									>
										<i className="ri-equalizer-3-fill m-0" />
									</Button>
								</div>
							</Form>
						</Col>
						<Col className="col-6 flex-row-right">
							<Pagination
								meta={meta}
								searchParams={searchParams}
								setSearchParams={setSearchParams}
								searchFields={searchFields}
								className="pull-right flex-row-left"
							/>
							<Button
								variant="light"
								className="refresh-btn flex-column no-margin"
								onClick={getData}
							>
								<i className="ri-restart-line opacity-7" />
							</Button>
						</Col>
					</div>
				</div>
				<Row className="content-table-header bg-white">
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">Name</span>
					</Col>
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">
							Mobile No.
						</span>
					</Col>
					<Col className="p-10 flex-row-right"></Col>
				</Row>
				{loading ? (
					<PageLoader className="bg-white p-50" />
				) : messages?.length === 0 ? (
					<Empty label="No messages to show." className="bg-white" />
				) : (
					messages.map((message) => (
						<Row className="content-table-item bg-white">
							<Col className="p-10 ellipsis">
								<span className="text-sm">{message.recipient_name}</span>
							</Col>
							<Col className="p-10 ellipsis">
								<span className="text-sm">{message.recipient_msisdn}</span>
							</Col>
							<Col className="flex-row-right">
								<span
									className={`recipient-status label-md text-medium-gotham text-white fit ${message.status}`}
								>
									{message.status}
								</span>
							</Col>
						</Row>
					))
				)}
			</div>
		</>
	);
};

export default AnnouncementRecipients;
